/**
 * skyAgent chat surface (SkyCRM) — ported from skySchedule's ChatPanel and
 * re-skinned onto SkyPoint components (D5: mirror skySchedule's chat UI).
 *
 * Layout mirrors the reference: a collapsible history rail, a centered hero
 * empty-state (glowing logo + starter command cards), the message thread with a
 * live activity strip, and a rounded composer pill.
 *
 * DESIGN-SYSTEM RULES APPLIED (skypoint-design-system):
 *   - Sky* components for every control: buttons are SkyButton, the row options
 *     menu is SkyMenu, cards are SkyCard. Styling is Tailwind only.
 *
 *     FOUR deliberate exceptions, and the list is exhaustive on purpose — an
 *     undocumented fifth is a regression, not a judgement call:
 *
 *       1. The history-rail ROW and 3. the starter-prompt CHIP are clickable
 *          SURFACES rather than controls: a block of text that happens to be
 *          activatable. Wrapping either in SkyButton fights its own layout and
 *          label semantics. Native <button type="button"> keeps them real buttons
 *          for keyboard and screen-reader users, which a clickable <div> is not.
 *       2. The COMPOSER is a bare <textarea>: it must auto-grow to its content and
 *          intercept Enter vs Shift+Enter, and SkyTextarea owns its own padding,
 *          which breaks the scrollHeight measurement that sizing depends on.
 *       4. The RENAME field is a bare <input> because `SkyInputProps` does not
 *          extend `InputHTMLAttributes` — it declares value/onChange/onBlur and no
 *          `onKeyDown` or `autoFocus`, and rename lives or dies on Enter-to-save
 *          and Escape-to-abandon. This is a real gap in the component, not a
 *          preference.
 *
 *     Everything else IS a Sky* component. In particular `appearance="ai"` is the
 *     design system's amber AI action — a hand-rolled `bg-amber-400` pill is that
 *     variant reinvented, minus its hover, active and focus-visible states.
 *   - Status is carried by icon AND text, never colour alone.
 *   - Motion stays in the 120-300ms band and honours prefers-reduced-motion via
 *     the `motion-reduce:` variants.
 *   - The centered hero is the ONE permitted centered block (empty state).
 *   - No emoji, no em-dashes in UI copy.
 *
 * SCOPE NOTES (what is deliberately NOT ported):
 *   - Quick prompts are a LOCAL constant, not a fetch. skySchedule reads
 *     /quick-prompts; Contract B defines no such route, so inventing one would
 *     be an unratified fork. Starter prompts are presentation only.
 *   - Rename / delete of a thread ARE supported (Contract B routes exist).
 *     Delete is a server-side soft delete and is not re-confirmed in the UI.
 *     Pin / archive are omitted — both would need a schema column.
 *   - Tool activity renders the server humanLabel ONLY. `chunk.toolCall.name`
 *     is never read, so a raw tool name or endpoint path cannot reach the UI.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import {
  SkyBadge,
  SkyBody1,
  SkyButton,
  SkyDivider,
  SkyInput,
  SkyLoader,
  SkyMenu,
  SkyMessageBar,
  SkySubtitle2,
  SkyTitle1,
} from "@skypointcloud/skypoint-ui";
import type { LucideIcon } from "lucide-react";
import {
  AlarmClock,
  ArrowUp,
  BarChart3,
  BookOpen,
  BrainCircuit,
  Building2,
  CalendarDays,
  Check,
  CircleCheck,
  Copy,
  Download,
  FileText,
  ListOrdered,
  MessageSquare,
  Mic,
  MoreHorizontal,
  Pencil,
  Trash2,
  PanelLeftClose,
  PanelLeftOpen,
  Plug,
  Plus,
  Search,
  Sparkles,
  Square,
  TrendingUp,
} from "lucide-react";
// ONE variant. The earlier two-image `dark:` swap was wrong twice over: `.hidden`
// is emitted after `.dark\:block` in the Tailwind v4 bundle at equal specificity,
// so the dark-only image could never win — and more fundamentally SkyCRM has NO
// dark theme (no theme provider, no `dark:` utilities outside this folder, and
// AppLayout hardcodes `bg-white`). The premise that it "themes on
// prefers-color-scheme" was simply false, so a working swap would have put the
// light-ink mark on a permanently white page. `-dark` is the DARK-INK artwork,
// which is the one that belongs on a white ground.
import skyAgentWordmark from "@/assets/logos/skyagent-wordmark-dark.png";
import {
  isCapabilityDisabled,
  useDeleteSkyAgentSessionMutation,
  useLazyGetSkyAgentSessionQuery,
  useListSkyAgentSessionsQuery,
  useRenameSkyAgentSessionMutation,
  type ChatSessionSummary,
} from "@/store/api/crmSkyAgentApi";
import { useAuthState } from "@/hooks/useAuth";
import { usePacks } from "@/components/packs/PackContext";
import { getInstanceIdFromUrl } from "@/utils/instanceId";
import { useSkyAgentChat, type StreamChatFn } from "./useSkyAgentChat";
import { useDictation } from "./useDictation";
import { groupSessionsByDate } from "./sessionGroups";
import { AgentMarkdown } from "./AgentMarkdown";
import { AgentActionCardContainer } from "./AgentActionCardContainer";
import { TurnNotices } from "./TurnNotices";
import { splitActionMarkers } from "./agentActionMarker";
import { splitPlaybookCitation, type CitedAnswer } from "../../../../shared/playbookCitation";
import ConnectorsMenu from "./ConnectorsMenu";
import ThreadPinControl from "./ThreadPinControl";
import SaveDraftButton from "./SaveDraftButton";

/**
 * The rail's quick links live under these two section headings.
 *
 * Matched by the section LABEL from the pack manifest rather than by route
 * prefix: skyBrain's Memory page sits under `/crm/skyagent/...` too, so a prefix
 * match would be right today by accident and wrong the moment either section
 * gains a page somewhere else.
 */
const RAIL_LINK_SECTIONS = new Set(["skyAgent", "skyBrain"]);

/** This page. It is where the reader already is, so it is not a link. */
const RAIL_LINK_SELF_ROUTE = "/crm/skyagent";

/**
 * Icons, keyed by route so they match the sidebar's.
 *
 * A small local map rather than an import from the sidebar's `packNav`: that
 * registry is not exported, and exporting it to decorate four rows would widen a
 * shared module's surface for a cosmetic reason. An unknown route falls back
 * rather than rendering nothing.
 */
const RAIL_LINK_ICONS: Record<string, LucideIcon> = {
  "/crm/skyagent/skills": BookOpen,
  "/crm/skyagent/drafts": FileText,
  "/crm/skyagent/connectors": Plug,
  "/crm/skyagent/brain": BrainCircuit,
};

/**
 * Starter prompts for the hero. Local by design (see header). Phrased as the
 * questions the read-only agent can actually answer in Increment 2.
 */
const STARTER_PROMPTS: { label: string; prompt: string; Icon: LucideIcon }[] = [
  {
    label: "Open pipeline",
    prompt: "How many open prospects do I have, broken down by stage?",
    Icon: BarChart3,
  },
  {
    label: "This week's tours",
    prompt: "Which tours are scheduled this week, and who is hosting them?",
    Icon: CalendarDays,
  },
  {
    label: "Past due activities",
    prompt: "Which activities are past due right now?",
    Icon: AlarmClock,
  },
  {
    label: "Occupancy",
    prompt: "What is current occupancy across my communities?",
    Icon: Building2,
  },
  {
    label: "Waitlist depth",
    prompt: "How deep is the waitlist by care type?",
    Icon: ListOrdered,
  },
  {
    label: "Move-ins this month",
    prompt: "How are move-ins tracking against goal this month?",
    Icon: TrendingUp,
  },
];

/**
 * Strip a truncation marker the SERVER baked into a stored title.
 *
 * `deriveTitle` used to cut at a character count and append an ellipsis. Those
 * titles are already in the database, and they are short enough to FIT the rail —
 * so CSS never truncates them and their stored "…" shows up wherever that row's
 * text happened to end. The result is a column of ragged endings that no amount
 * of styling fixes, because the raggedness is in the data.
 *
 * Stripping it here makes CSS `truncate` the only truncation the eye ever sees,
 * so every clipped row ends at the same x. New titles no longer carry the marker
 * at all; this is what makes the rows written before that change look the same.
 *
 * ONLY the U+2026 character, and deliberately not `...`. Titles are user-editable
 * now, and three dots is something a person types on purpose — "Follow up with the
 * Harpers..." is a title, not a truncation. `deriveTitle` only ever wrote the
 * single-character ellipsis, so matching exactly that is the difference between
 * cleaning up after ourselves and quietly editing someone's words.
 */
function cleanTitle(title: string | null | undefined): string {
  return (title ?? "").replace(/\s*…\s*$/, "").trim();
}

const dayLabel = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }).toUpperCase()
    : "";

export interface ChatPanelProps {
  /** Community scope echoed on every turn so follow-ups stay scoped. */
  context?: string;
  /**
   * Transport override. Supplying the mock stream runs the whole panel with no
   * server, which is how Track B develops and tests ahead of Track A.
   */
  streamChat?: StreamChatFn;
  createSession?: () => Promise<string>;
  /** Hides the history rail when the panel is hosted in a narrow drawer. */
  showHistoryRail?: boolean;
  /**
   * A chat to open on arrival, from a deep link elsewhere in the CRM (a pinned
   * thread on a prospect's record). Opened once per id; the host clears the URL
   * parameter after handing it over so a refresh does not re-open it and fight
   * "New task". Null/undefined means the normal empty-state landing.
   */
  initialSessionId?: string | null;
}

export function ChatPanel({
  context,
  streamChat,
  createSession,
  showHistoryRail = true,
  initialSessionId,
}: ChatPanelProps) {
  const {
    messages,
    activity,
    isStreaming,
    error,
    currentSessionId,
    send,
    stop,
    reset,
    loadSession,
  } = useSkyAgentChat({ context, streamChat, createSession });

  const [input, setInput] = useState("");
  /**
   * Chat vs Cowork — the read/write intent for THIS conversation.
   *
   * `chat` suppresses the propose_ tools for the turn, so the agent answers and
   * advises but never offers to change anything. `cowork` leaves the catalog as
   * the tenant's capability allows.
   *
   * Read-only is the DEFAULT deliberately: the safe posture should be the one you
   * get without choosing. Remembered per browser rather than per session — this is
   * a UI preference, and a schema column for it would be a migration to store
   * which button someone last pressed.
   */
  const [mode, setMode] = useState<"chat" | "cowork">(() => {
    try {
      return localStorage.getItem("skyagent.mode") === "cowork" ? "cowork" : "chat";
    } catch {
      // Private windows and blocked site-data throw on ACCESS, not just on write.
      return "chat";
    }
  });
  /**
   * Copy an answer. Strips the action markers first — the marker is server-only
   * plumbing that renders as a CARD, so pasting it into an email would paste a
   * token the reader cannot act on and we have spent a lot of effort keeping off
   * screens in the first place.
   */
  /**
   * Split the playbook citation off an answer, memoised per string.
   *
   * Called several times per message during a render, and a stream re-renders on
   * every chunk — so the cache is what keeps this from re-parsing the same
   * growing answer on each frame. Keyed by the exact text, so a changed answer is
   * simply a different key.
   */
  /**
   * The other skyAgent/skyBrain surfaces, for the rail.
   *
   * Read off the installed-pack projection, which is already capability-filtered
   * server-side — so this list is empty for a customer with none of them switched
   * on, and the block above the search box simply is not there.
   */
  const navigate = useNavigate();
  const instanceId = getInstanceIdFromUrl() || "";
  const { packs } = usePacks();
  const railLinks = useMemo(
    () =>
      packs
        .filter((p) => p.mode === "operator_crm")
        .flatMap((p) => p.sidebarSections ?? [])
        .filter((section) => RAIL_LINK_SECTIONS.has(section.label))
        .flatMap((section) => section.items)
        .filter((item) => item.route && item.route !== RAIL_LINK_SELF_ROUTE),
    [packs],
  );

  const citationCache = useRef(new Map<string, CitedAnswer>());
  const answerOf = (content: string): CitedAnswer => {
    const hit = citationCache.current.get(content);
    if (hit) return hit;
    const split = splitPlaybookCitation(content);
    // Bounded: a long thread with a long stream would otherwise grow a map entry
    // per keystroke of output. Oldest out first; a re-parse is cheap.
    if (citationCache.current.size > 200) {
      const oldest = citationCache.current.keys().next().value;
      if (oldest !== undefined) citationCache.current.delete(oldest);
    }
    citationCache.current.set(content, split);
    return split;
  };

  const copyAnswer = async (id: string, content: string) => {
    // The citation comes off FIRST, for the same reason the action markers do:
    // this text is going somewhere else, and it is our plumbing.
    const plain = splitActionMarkers(answerOf(content).body)
      .map((part) => (part.kind === "text" ? part.text : ""))
      .join("")
      .trim();
    try {
      await navigator.clipboard.writeText(plain);
      setCopiedId(id);
      window.setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1500);
    } catch {
      // Clipboard is permission-gated and absent over plain http. Failing quietly
      // is right: the answer is still on screen and selectable by hand.
    }
  };

  /** Dictation appends to whatever is already typed rather than replacing it, so
   *  speaking after typing extends the question instead of eating it. */
  const dictation = useDictation((text) =>
    setInput((cur) => (cur.trim() ? `${cur.trim()} ${text}` : text)),
  );

  /**
   * Save one answer as a .md file.
   *
   * Same plain text the copy button produces — action markers stripped, because a
   * saved file is the version most likely to be forwarded, and a `[SKYAGENT_ACTION:…]`
   * token in someone's inbox is machinery escaping into a document.
   *
   * `.md` rather than `.txt`: answers are markdown (tables, bold), and a .txt of a
   * pipeline table is a wall of pipes.
   */
  const downloadAnswer = (content: string) => {
    // Citation off first — a downloaded file is as pasteable as a copied one.
    const plain = splitActionMarkers(answerOf(content).body)
      .map((part) => (part.kind === "text" ? part.text : ""))
      .join("")
      .trim();
    if (!plain) return;
    const url = URL.createObjectURL(new Blob([plain], { type: "text/markdown;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `skyagent-answer-${new Date().toISOString().slice(0, 10)}.md`;
    // APPENDED, and revoked on a later task. Both matter, and both are the classic
    // ways a download silently does nothing:
    //   - a detached <a> is not in the document, and a click on it is ignored
    //     outside Chrome rather than starting a download.
    //   - `URL.revokeObjectURL` on the same tick can invalidate the blob before the
    //     browser has finished reading it, so the file arrives empty or not at all.
    // Neither failure throws, and neither is visible in a Chrome-only smoke test,
    // which is exactly why they are worth spelling out rather than trusting.
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    window.setTimeout(() => {
      a.remove();
      URL.revokeObjectURL(url);
    }, 0);
  };

  const chooseMode = (next: "chat" | "cowork") => {
    setMode(next);
    try {
      localStorage.setItem("skyagent.mode", next);
    } catch {
      // A preference that cannot be remembered is still a preference that works.
    }
  };
  const [railOpen, setRailOpen] = useState(showHistoryRail);
  const [sessionSearch, setSessionSearch] = useState("");
  /**
   * Which row is being renamed. One at a time — two open editors in a list is
   * never what anyone meant.
   *
   * There is no `menuId` beside it any more. The options menu was hand-rolled: a
   * `role="menu"` div, a `menuId` state, and a `pointerdown`/Escape listener bound
   * to `window` to dismiss it, with `stopPropagation` on three elements to stop
   * that listener eating its own clicks. `SkyMenu` is the design system's menu and
   * owns all of it — dismissal, focus return, roving arrow keys and typeahead,
   * none of which the hand-rolled version had. See the header's Sky*-first rule:
   * the exceptions in this file are the ones the components genuinely cannot
   * express, and a menu was never one of them.
   */
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameSession] = useRenameSkyAgentSessionMutation();
  const [deleteSession] = useDeleteSkyAgentSessionMutation();

  /** Save a rename. An empty or unchanged title is a no-op, not an error — the
   *  commonest way to leave this field is to click away without meaning anything. */
  const commitRename = async (sessionId: string, title: string) => {
    setRenamingId(null);
    const next = title.trim();
    if (!next) return;
    try {
      await renameSession({ sessionId, title: next }).unwrap();
      void sessionsQ.refetch();
    } catch {
      setLoadError("That chat could not be renamed.");
    }
  };

  /**
   * Delete a thread. Deliberately NOT confirmed: opening the menu and choosing
   * Delete is already a two-step gesture, and the server soft-deletes, so
   * nothing is actually destroyed. A native `window.confirm` is also not a
   * design-system dialog and does not belong in this app.
   */
  const removeThread = async (sessionId: string) => {
    try {
      await deleteSession(sessionId).unwrap();
      // If the open thread was the one deleted, the panel must not keep showing
      // a conversation the rail no longer lists.
      if (sessionId === currentSessionId) reset();
      void sessionsQ.refetch();
    } catch {
      setLoadError("That chat could not be deleted.");
    }
  };

  /** Which answer just got copied, so the tick can replace the icon briefly. */
  const [copiedId, setCopiedId] = useState<string | null>(null);

  /**
   * How long the last turn took, in seconds, keyed by the answer it belongs to.
   *
   * Measured on the CLIENT — wall time from send to the end of the stream — and
   * labelled as such, because that is the number the person actually waited. The
   * server's own duration would be smaller and would quietly disagree with the
   * clock they were watching.
   */
  /**
   * The one answer being written right now, or null.
   *
   * Only the LAST assistant message can be the streaming one — the transport
   * appends a placeholder and fills it. Deriving it here rather than gating on
   * `isStreaming` alone is what keeps the copy/download controls attached to the
   * answers that are already finished.
   */
  const streamingMsgId = useMemo(
    () =>
      isStreaming
        ? ([...messages].reverse().find((m) => m.role === "assistant")?.id ?? null)
        : null,
    [isStreaming, messages],
  );
  /** The newest answer, streaming or not — the one the hook's `error` belongs
   *  to, since `error` is cleared on every send. */
  const lastAssistantId = useMemo(
    () => [...messages].reverse().find((m) => m.role === "assistant")?.id ?? null,
    [messages],
  );

  const [elapsedById, setElapsedById] = useState<Record<string, string>>({});
  const turnStartedAt = useRef<number | null>(null);
  // A SECOND streaming latch, deliberately separate from the rail's. Sharing one
  // ref would make these two effects order-dependent on each other, and they are
  // unrelated concerns — one refreshes a list, this one stops a stopwatch.
  const timingWasStreaming = useRef(false);

  useEffect(() => {
    if (isStreaming && !timingWasStreaming.current) {
      turnStartedAt.current = Date.now();
    }
    if (!isStreaming && timingWasStreaming.current && turnStartedAt.current !== null) {
      const secs = (Date.now() - turnStartedAt.current) / 1000;
      const last = [...messages].reverse().find((m) => m.role === "assistant");
      if (last) {
        setElapsedById((prev) => ({ ...prev, [last.id]: secs.toFixed(1) }));
      }
      turnStartedAt.current = null;
    }
    timingWasStreaming.current = isStreaming;
  }, [isStreaming, messages]);

  /**
   * Switching community abandons the open thread.
   *
   * A conversation is scoped by the community it was asked in — every count in it
   * was computed under that scope. Leaving it on screen after the header changes
   * shows figures that are no longer true for what the header now claims, and the
   * NEXT question in the same thread would be answered under the new scope while
   * sitting beneath answers from the old one. Starting fresh is the honest move.
   *
   * A bare guarded selector rather than `useSelectedCommunity`: that hook also
   * dispatches and issues a query, and this panel is designed to render with no
   * store at all (its tests mock `useSelector` as `() => undefined`).
   */
  const selectedCommunityId = useSelector(
    (st: { communityReducer?: { selectedCommunityId?: string | null } } | undefined) =>
      st?.communityReducer?.selectedCommunityId ?? null,
  );
  const lastCommunity = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    // First render establishes the baseline; it is not a change.
    if (lastCommunity.current === undefined) {
      lastCommunity.current = selectedCommunityId ?? null;
      return;
    }
    if (lastCommunity.current !== (selectedCommunityId ?? null)) {
      lastCommunity.current = selectedCommunityId ?? null;
      reset();
      setElapsedById({});
    }
  }, [selectedCommunityId, reset]);
  // Grow the bare textarea with its content, capped at the CSS max-height.
  // Safe here in a way it was not on SkyTextarea: this element has no padding of
  // its own, so scrollHeight is exactly the content height.
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [input]);

  /**
   * Rail page size. Pages are KEYSET-cursored, not offset: `last_activity_at`
   * moves every time a thread is used, so an offset page 2 would re-show a row
   * from page 1 and silently drop the one at the boundary. `cursor` is opaque and
   * comes back from the server as `next_cursor`; null means "start at the top".
   */
  const SESSION_PAGE = 20;
  const [sessionCursor, setSessionCursor] = useState<string | null>(null);
  /** Surfaced when opening a past thread fails; cleared on the next attempt. */
  const [loadError, setLoadError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  // Mock mode drives the panel without a server, so the session endpoints must
  // not be called either.
  const isMock = Boolean(streamChat);

  const sessionsQ = useListSkyAgentSessionsQuery(
    { limit: SESSION_PAGE, cursor: sessionCursor },
    { skip: isMock },
  );
  const [fetchSession] = useLazyGetSkyAgentSessionQuery();

  const capabilityOff = isCapabilityDisabled(sessionsQ.data);

  /** Every thread the server returned, BEFORE the search box narrows it. The
   *  rail renders the filtered list, but anything that answers a question about
   *  the OPEN thread has to read this one — a search that happens to exclude the
   *  open thread must not change what the thread header says about it. */
  const allSessions = useMemo<ChatSessionSummary[]>(() => {
    const data = sessionsQ.data;
    return data && !("enabled" in data && data.enabled === false)
      ? ((data as { sessions?: ChatSessionSummary[] }).sessions ?? [])
      : [];
  }, [sessionsQ.data]);

  const sessions = useMemo<ChatSessionSummary[]>(() => {
    const q = sessionSearch.trim().toLowerCase();
    // `cleanTitle`, not `s.title` — `title` is nullable on the wire (a thread has
    // none until its first answer derives one), so `s.title.toLowerCase()` threw
    // and blanked the whole rail the moment anyone typed while an empty thread
    // was in the list. It also means search matches what the row DISPLAYS.
    return q
      ? allSessions.filter((s) => cleanTitle(s.title).toLowerCase().includes(q))
      : allSessions;
  }, [allSessions, sessionSearch]);

  /**
   * The community THIS conversation is scoped to, for the thread header.
   *
   * Read off the rail row for the open thread rather than off the picker: a thread
   * carries the scope it was STARTED in, and every number already in it was
   * computed under that scope. They agree today because switching community resets
   * the thread — but the header should state the conversation's own scope, not
   * infer it from a control that can move independently later.
   *
   * THREE cases, not two. A thread opened across everything and a thread whose
   * community has since been removed both arrive here with no name, and calling
   * them both "All communities" is the one answer that is wrong for one of them:
   * it tells a counselor a number covers the whole portfolio when it covered a
   * single community that is no longer listed. `communityId` is what separates
   * them — it is present either way, and only the NAME goes missing.
   */
  const openThreadScope = useMemo(() => {
    if (!currentSessionId) return null;
    const row = allSessions.find((x) => x.sessionId === currentSessionId);
    if (!row || row.communityId === undefined) return null;
    if (!row.communityId) return "All communities";
    return row.communityName ?? "A community that is no longer listed";
  }, [allSessions, currentSessionId]);

  /** The cursor for the NEXT page, or null at the end. The server's `null` is the
   *  single "stop" signal — the rail never infers the end from a row count, which
   *  would keep offering "Show more" on an exactly-full final page. */
  const nextSessionCursor =
    sessionsQ.data && !("enabled" in sessionsQ.data && sessionsQ.data.enabled === false)
      ? ((sessionsQ.data as { next_cursor?: string | null }).next_cursor ?? null)
      : null;
  const hasMoreSessions = Boolean(nextSessionCursor);

  /**
   * Group headers ("Today", "Yesterday", "Previous 7 Days", …) computed off the
   * SERVER's ordering — `groupSessionsByDate` deliberately does not re-sort, so a
   * server ordering bug shows up as jumbled headers rather than being hidden.
   *
   * Recomputed against a stable `now` per render pass. Not memoized on a clock:
   * the rail re-renders on data change, and a "Today" header that survives
   * midnight until the next fetch is a smaller wrong than a timer re-render.
   */
  const groupedSessions = useMemo(
    () => groupSessionsByDate(sessions, (s) => s.lastActivityAt),
    [sessions],
  );

  /** Pull the next page when the rail nears its end (skySchedule's rail idiom).
   *  Guarded on `isFetching` so a fast scroll cannot queue several pages. */
  const loadMoreSessions = () => {
    if (!nextSessionCursor || sessionsQ.isFetching) return;
    setSessionCursor(nextSessionCursor);
  };

  // Refresh the rail once a turn finishes, so a new thread appears and titles
  // update without the user reloading.
  const wasStreaming = useRef(false);
  useEffect(() => {
    if (wasStreaming.current && !isStreaming && !isMock) {
      // Rewind to the FIRST page, not refetch the current one. A finished turn
      // creates or bumps a thread at the TOP of the rail; refetching whatever
      // cursor the user had scrolled to would fetch a middle page, `merge` would
      // append it (deduping to nothing), and the new thread would never appear.
      // Setting the cursor to null makes the next fetch a first page, which
      // `merge` treats as a REPLACE.
      if (sessionCursor !== null) setSessionCursor(null);
      else sessionsQ.refetch();
    }
    wasStreaming.current = isStreaming;
    // sessionsQ is deliberately not a dep: the query object is a new reference on
    // every render, and depending on it would refetch the list on each keystroke.
  }, [isStreaming, isMock]);

  // Keep pinned to the newest content as it streams in.
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messages, activity]);

  const submit = (text: string) => {
    if (!text.trim() || isStreaming) return;
    // The mode is read at SEND time, not at mount: flipping the switch takes
    // effect on the very next message rather than on the next thread.
    void send(text, mode);
    setInput("");
  };

  const openThread = async (sessionId: string) => {
    setLoadError(null);
    try {
      const res = await fetchSession(sessionId).unwrap();
      if ("enabled" in res && res.enabled === false) return;
      loadSession(
        sessionId,
        (res as { messages?: { role: "user" | "assistant"; content: string }[] }).messages ?? [],
      );
    } catch {
      // .unwrap() throws on any failure. Unhandled, the row just did nothing and
      // logged an unhandled rejection; the user got no feedback at all.
      setLoadError("That chat could not be opened. It may have been removed.");
    }
  };

  // Arriving from a deep link (a pinned chat on a prospect's record). Opened
  // exactly once per id, tracked by ref rather than by a dependency array: the
  // user may close the thread and start a new one without the id changing, and
  // re-running the effect would yank them back to the pinned chat.
  const deepLinkedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!initialSessionId || deepLinkedRef.current === initialSessionId) return;
    deepLinkedRef.current = initialSessionId;
    void openThread(initialSessionId);
    // Deliberately keyed on the id alone. `openThread` is redefined every
    // render, so listing it would re-fire this on every render.
  }, [initialSessionId]);

  const startNewThread = () => {
    // Reset only. Do NOT create a session here: useSkyAgentChat already creates
    // one lazily on the first send, so creating one now wrote an empty
    // "untitled" row for every click of New task — the thread has no title until
    // a first message exists to derive one from.
    reset();
    setInput("");
  };

  const isEmpty = messages.length === 0;

  // The greeting name. Read from the id-token claims the app already holds — the
  // CRM header greets by the same value, so this cannot drift from it, and no
  // shared module is touched to get it. `name` is an OPTIONAL claim, so every
  // consumer has to tolerate its absence rather than assume a string.
  //
  // `useAuthState` (a bare selector) and NOT `useAuth`. `useAuth` is the auth
  // CONTROLLER — it pulls in `useDispatch`, sign-out, token refresh — and reaching
  // for it to read one string coupled this panel to dispatch it never calls.
  //
  // NOT DESTRUCTURED. This panel is designed to render with NO STORE: its tests
  // mock `useSelector` as `() => undefined` on purpose, and the drawer mounts it
  // in contexts that do the same. Destructuring assumed an object and threw for
  // every test in the file. Two things are independently optional here — the
  // store slice AND the `name` claim within it — so both are guarded.
  const authState = useAuthState() as { idTokenClaims?: { name?: string } } | undefined;
  const displayName = String(authState?.idTokenClaims?.name ?? "").trim();

  if (capabilityOff) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-sm text-center">
          <Sparkles
            className="mx-auto h-8 w-8 text-muted-foreground"
            aria-hidden="true"
          />
          <SkySubtitle2 className="mt-3 block">skyAgent is turned off</SkySubtitle2>
          <SkyBody1 className="mt-1 block text-muted-foreground">
            An administrator can enable it in AI Governance.
          </SkyBody1>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full">
      {/* ── History rail ─────────────────────────────────────────────── */}
      {showHistoryRail && (
        // Width transition rather than a conditional unmount: removing the node
        // outright made the rail vanish in one frame and the thread jump sideways.
        // Kept mounted at w-0 with overflow clipped, and hidden from assistive
        // tech so a collapsed rail is not tabbable.
        // `flex!` and `flex-col!`, not `flex flex-col` — and this is not cargo cult.
        // Tailwind v4 emits utilities inside `@layer utilities`, and an UNLAYERED
        // rule beats any layered one regardless of specificity. `skypointcloud.css`
        // carries the old HTML5 reset `article, aside, details, … { display: block }`
        // unlayered, so it silently won: this element computed to `display: block`,
        // `flex-col` did nothing, and the nav's `flex-1 min-h-0` had no flex parent
        // to bound it. The rail therefore grew to its content height (685px inside a
        // 536px box) and `overflow-hidden` CLIPPED the older chats out of reach with
        // no scrollbar — visible as "the list just stops". Verified in the browser,
        // not inferred. `hidden! md:flex!` in header.tsx is the same workaround for
        // the same cause, so this is the house idiom.
        <aside
          className={`flex! shrink-0 flex-col! overflow-hidden border-border transition-[width] duration-200 ease-out motion-reduce:transition-none ${
            railOpen ? "w-64 border-r" : "w-0 border-r-0"
          }`}
          aria-hidden={!railOpen}
          inert={!railOpen ? true : undefined}
        >
          {/* Wordmark row, then a FULL-WIDTH primary action, then the nav —
              the reference's order. The collapse control sits beside the mark
              rather than beside the button, which is what frees the button to run
              the width of the rail. */}
          <div className="flex h-14 w-64 shrink-0 items-center justify-between gap-2 px-3">
            <img
              src={skyAgentWordmark}
              alt="skyAgent"
              className="h-7 w-auto"
            />
            <SkyButton
              appearance="transparent"
              size="small"
              title="Hide history"
              aria-label="Hide history"
              onClick={() => setRailOpen(false)}
            >
              <PanelLeftClose
                className="h-4 w-4"
                aria-hidden="true"
              />
            </SkyButton>
          </div>

          <div className="w-64 px-3 pb-3">
            {/* `appearance="ai"` rather than a hand-rolled amber pill. The design
                system's AI variant IS `bg-amber-400` — this was reinventing it,
                and getting two details wrong on the way: `hover:bg-amber-300`
                brightens on hover where every other button in the app darkens, and
                a bare <button> has no focus-visible ring at all, so the rail's
                primary action was unreachable-looking by keyboard. */}
            <SkyButton
              appearance="ai"
              onClick={startNewThread}
              className="w-full"
            >
              <Plus
                className="h-4 w-4"
                aria-hidden="true"
              />
              New task
            </SkyButton>
          </div>

          {/* THE SPACE ABOVE SEARCH, which was empty.
              The reference product puts its other surfaces here — the workspace
              is where you are, and the things around it are one click away
              rather than a trip to the sidebar.

              These come from the SAME projection the sidebar renders
              (`/packs/installed` sidebarSections), not from a list written here.
              That matters: those items are already filtered server-side by
              capability, so a surface that is switched off is ABSENT rather than
              present-and-refusing — and a second hardcoded list would be a
              second thing to keep in step with the manifest, which is how these
              rows end up pointing at pages that answer "not switched on".

              Workspace itself is dropped: it is this page. */}
          {railLinks.length > 0 ? (
            <div className="px-3 pb-2">
              <ul className="flex flex-col gap-0.5">
                {railLinks.map((link) => {
                  const Icon = RAIL_LINK_ICONS[link.route] ?? Sparkles;
                  return (
                    <li key={link.route}>
                      <button
                        type="button"
                        onClick={() => navigate(`${link.route}/${instanceId}`)}
                        className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground motion-reduce:transition-none"
                      >
                        <Icon
                          className="h-4 w-4 shrink-0"
                          aria-hidden="true"
                        />
                        <span className="truncate">{link.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

          <div className="px-3 pb-2">
            <SkyInput
              value={sessionSearch}
              placeholder="Search chats..."
              contentBefore={
                <Search
                  className="h-3.5 w-3.5"
                  aria-hidden="true"
                />
              }
              onChange={(e) => setSessionSearch(e.target.value)}
            />
          </div>

          <SkyDivider />

          <nav
            // `scrollbar-hide` (src/index.css) hides the scrollbar CHROME without
            // touching scrollability — the rail still scrolls by wheel, trackpad,
            // touch, and keyboard, and `overflow-y: auto` still makes it a focusable
            // scroll container for screen readers. Only the always-on grey track
            // beside a 240px rail goes away.
            className="min-h-0 flex-1 overflow-y-auto scrollbar-hide p-2"
            aria-label="Chat history"
            onScroll={(e) => {
              // Load older chats as the rail nears the bottom. Skipped while
              // searching: search filters the already-loaded pages client-side,
              // so auto-paging there would silently fetch pages the user cannot
              // see the effect of.
              if (sessionSearch.trim()) return;
              const el = e.currentTarget;
              if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) loadMoreSessions();
            }}
          >
            {sessionsQ.isLoading && (
              <div className="flex items-center gap-2 p-2 text-sm text-muted-foreground">
                <SkyLoader size="tiny" /> Loading chats
              </div>
            )}
            {!sessionsQ.isLoading && sessions.length === 0 && (
              <p className="p-2 text-sm text-muted-foreground">No chats yet.</p>
            )}
            <ul className="space-y-0.5">
              {groupedSessions.map(({ item: s, header }) => (
                <li key={s.sessionId}>
                  {/* The date separator. Rendered inside the <li> rather than as
                      its own list item so the list stays a flat list of chats for
                      a screen reader — the header is a label on the group, not a
                      chat you can open. */}
                  {header && (
                    <div
                      role="presentation"
                      className="px-2 pb-1 pt-3 text-sm font-normal uppercase tracking-wide text-muted-foreground"
                    >
                      {header}
                    </div>
                  )}
                  {/* The row is a BUTTON with a SIBLING menu, not a button
                      containing one: a <button> inside a <button> is invalid HTML
                      and the inner click would open the thread as well as the
                      menu. `group` keeps the trigger hidden until hover/focus
                      without JS, and `focus-within` keeps it reachable by
                      keyboard, which a hover-only control never is. */}
                  <div className="group relative flex items-center">
                    {renamingId === s.sessionId ? (
                      /* A documented exception to the Sky*-components rule, for the
                         same reason as the composer's bare <textarea>:
                         `SkyInputProps` does NOT extend
                         `InputHTMLAttributes` — it declares value/onChange/onBlur
                         and stops there, with no `onKeyDown` and no `autoFocus`.
                         Rename lives or dies on Enter-to-save and
                         Escape-to-abandon, so the component genuinely cannot
                         express it. (`SkyButtonProps` DOES extend its DOM props,
                         which is why every button here is a SkyButton.) Styled to
                         the app's own tokens so it still looks like the system.

                         It replaces the row entirely rather than sitting beside it.
                         The row button used to stay mounted with its label nulled
                         out, which left a ~40px unlabelled click target next to the
                         field that silently opened the thread mid-rename. */
                      <div className="w-full">
                        {/* METRICS COPIED FROM THE ROW, not chosen freshly:
                            `py-1.5`, `text-sm font-medium`. Picking my own made the
                            field a different height and weight, so the row jumped
                            when you started renaming.

                            No `pr-8` — that space exists to keep the title clear of
                            the options button, and that button is gone while the
                            field is open, so reserving it just pushed the text off
                            centre. A resting amber border read as a validation
                            error; the app's own border with an amber FOCUS state is
                            what every other field here does. */}
                        <input
                          autoFocus
                          defaultValue={cleanTitle(s.title)}
                          aria-label="Rename chat"
                          className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm font-medium outline-none focus:border-amber-500"
                          onClick={(e) => e.stopPropagation()}
                          onBlur={(e) => void commitRename(s.sessionId, e.currentTarget.value)}
                          onKeyDown={(e) => {
                            if (e.nativeEvent.isComposing) return;
                            if (e.key === "Enter") {
                              e.preventDefault();
                              void commitRename(s.sessionId, e.currentTarget.value);
                            }
                            // Escape ABANDONS. Without it the only way out of an
                            // accidental rename is to blur, which SAVES.
                            if (e.key === "Escape") {
                              e.preventDefault();
                              setRenamingId(null);
                            }
                          }}
                        />
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => void openThread(s.sessionId)}
                          className={`flex min-w-0 flex-1 items-start gap-2 rounded-lg py-1.5 pl-2 pr-8 text-left transition-colors duration-150 motion-reduce:transition-none hover:bg-muted${
                            s.sessionId === currentSessionId ? " bg-muted" : ""
                          }`}
                        >
                          <span
                            className={`min-w-0 flex-1 truncate text-sm font-normal ${
                              s.sessionId === currentSessionId
                                ? "text-foreground"
                                : "text-muted-foreground"
                            }`}
                            title={`${cleanTitle(s.title) || "Untitled chat"} · ${dayLabel(s.lastActivityAt)}`}
                          >
                            {cleanTitle(s.title) || "Untitled chat"}
                          </span>
                        </button>

                        {/* SkyMenu, not a hand-rolled popover. It owns dismissal,
                            focus return to the trigger, arrow-key roving and
                            typeahead — the parts a `role="menu"` div and a window
                            listener do not get for free. `danger` is the design
                            system's own destructive styling, so Delete does not
                            need a hardcoded red.

                            `opacity-0 group-hover/group-focus-within` is kept on the
                            TRIGGER so the rail stays quiet at rest; a menu that is
                            open while the pointer has moved away is handled by
                            SkyMenu itself. */}
                        <SkyMenu
                          align="end"
                          trigger={
                            <SkyButton
                              appearance="transparent"
                              size="small"
                              aria-label={`Options for ${cleanTitle(s.title) || "this chat"}`}
                              className="absolute right-1 px-1 text-muted-foreground opacity-0 transition-opacity duration-150 group-focus-within:opacity-100 group-hover:opacity-100 focus-visible:opacity-100 motion-reduce:transition-none"
                            >
                              <MoreHorizontal
                                className="h-4 w-4"
                                aria-hidden="true"
                              />
                            </SkyButton>
                          }
                          items={[
                            {
                              key: "rename",
                              label: "Rename",
                              icon: (
                                <Pencil
                                  className="h-3.5 w-3.5"
                                  aria-hidden="true"
                                />
                              ),
                              onClick: () => setRenamingId(s.sessionId),
                            },
                            {
                              key: "delete",
                              label: "Delete",
                              danger: true,
                              icon: (
                                <Trash2
                                  className="h-3.5 w-3.5"
                                  aria-hidden="true"
                                />
                              ),
                              onClick: () => void removeThread(s.sessionId),
                            },
                          ]}
                        />
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {/* Only when the SERVER says another page exists — never inferred
                from the row count, which would keep offering "Show more" on an
                exactly-full final page. Search filters client-side, so it is
                hidden while searching to avoid implying the search is paged. */}
            {/* Fetching an OLDER page: an inline spinner at the end of the list,
                because with the scrollbar hidden there is otherwise no signal at
                all that scrolling to the bottom did anything. `isFetching` (not
                `isLoading`) is the right flag — `isLoading` is only true for the
                very first load, so it would never fire for a follow-on page. */}
            {sessionsQ.isFetching && sessionCursor !== null && (
              <div className="flex items-center gap-2 px-2 py-3 text-sm text-muted-foreground">
                <SkyLoader size="tiny" /> Loading older chats
              </div>
            )}

            {hasMoreSessions && !sessionSearch.trim() && !sessionsQ.isFetching && (
              <div className="px-2 pt-2">
                {/* Kept alongside the scroll handler rather than replaced by it:
                    scroll-to-load is unreachable by keyboard and does not fire
                    when the rail is taller than its content, which is exactly the
                    case on a short second page. */}
                <SkyButton
                  appearance="transparent"
                  size="small"
                  onClick={loadMoreSessions}
                >
                  Show more
                </SkyButton>
              </div>
            )}
          </nav>
        </aside>
      )}

      {/* ── Thread ───────────────────────────────────────────────────── */}
      <div
        className={
          isEmpty
            ? "flex min-h-0 min-w-0 flex-1 flex-col justify-center"
            : "flex min-h-0 min-w-0 flex-1 flex-col"
        }
      >
        {showHistoryRail && !railOpen && (
          <div className="p-2">
            <SkyButton
              appearance="transparent"
              size="small"
              title="Show history"
              aria-label="Show history"
              onClick={() => setRailOpen(true)}
            >
              <PanelLeftOpen
                className="h-4 w-4"
                aria-hidden="true"
              />
            </SkyButton>
          </div>
        )}

        {/* The open conversation's SCOPE, top-left, above the thread. Not on every
            rail row: twenty rows repeating one community name is noise, whereas the
            question "which communities is THIS answer about" is live exactly while
            you are reading it. Hidden on the empty state — there is no
            conversation to scope yet. */}
        {!isEmpty && openThreadScope ? (
          <div className="flex shrink-0 items-center gap-1.5 px-4 pb-1 pt-2">
            <Building2
              className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
            <span className="truncate text-sm text-muted-foreground">{openThreadScope}</span>
          </div>
        ) : null}

        {/* EMPTY: this area shrinks to its content so the hero and the composer
            below it sit together in the vertical middle, as skyCommand does.
            IN A THREAD: it takes the remaining height and scrolls, which pins the
            composer to the bottom where it belongs once there is history to read.
            One class decides it, so the two states cannot drift apart. */}
        <div
          className={
            isEmpty ? "flex shrink-0 flex-col px-4" : "min-h-0 flex-1 overflow-y-auto px-4"
          }
        >
          {isEmpty ? (
            /* The one permitted centered block: the empty state.
               Deliberately bare: greeting, composer, chips. The wordmark and the
               explanatory paragraph that used to sit here are gone — the reference
               puts nothing between the greeting and the thing you type into, and
               every line above the composer pushes it further from the eye. */
            <div className="flex flex-col items-center justify-center text-center">
              {/* SkyTitle1, not a raw `text-3xl`. The app's type scale lives in
                  the Sky* components (SkyTitle1/SkyBody1 are used ~1000 times
                  across these views); hand-picking a Tailwind size here made the
                  one screen that ignores the scale the one people look at first. */}
              <div className="mb-8 flex items-center justify-center gap-3">
                <Sparkles
                  className="h-7 w-7 shrink-0 text-amber-500"
                  aria-hidden="true"
                />
                <SkyTitle1
                  as="h1"
                  className="tracking-tight"
                >
                  Hey there{displayName ? `, ${displayName}` : ""}
                </SkyTitle1>
              </div>
            </div>
          ) : (
            <div className="mx-auto max-w-3xl py-4">
              {messages.map((m) =>
                m.role === "user" ? (
                  <div
                    key={m.id}
                    className="mb-4 flex justify-end"
                  >
                    <div className="max-w-[80%] rounded-2xl bg-muted px-3 py-2">
                      <SkyBody1 className="block whitespace-pre-wrap">{m.content}</SkyBody1>
                    </div>
                  </div>
                ) : (
                  <div
                    key={m.id}
                    className="mb-6"
                  >
                    {/* WHO IS SPEAKING. Without this the thread is a wall of
                        unattributed prose: the user's turn has a bubble, the
                        agent's had nothing at all, so a long answer read as page
                        content rather than as a reply. The reference heads every
                        answer the same way. */}
                    <div className="mb-1.5 flex items-center gap-1.5">
                      <Sparkles
                        className="h-4 w-4 shrink-0 text-amber-500"
                        aria-hidden="true"
                      />
                      <span className="text-sm font-semibold">skyAgent</span>
                    </div>

                    {/*
                      An action reaches the UI as a `[SKYAGENT_ACTION:<uuid>]`
                      marker inside the text (wire contract §4). The marker is
                      stripped and replaced by the card, in place — so the card
                      appears exactly where the agent put it in its answer, and
                      the id itself never reaches the screen.

                      The card fetches the action by id rather than rendering
                      anything carried in the message, which is what makes a
                      re-opened thread honest: yesterday's proposal shows as
                      executed or expired instead of offering an Approve button
                      that would fail.
                    */}
                    {answerOf(m.content).body
                      ? splitActionMarkers(answerOf(m.content).body).map((part, i) =>
                          part.kind === "action" ? (
                            <AgentActionCardContainer
                              key={`${m.id}-action-${part.actionId}`}
                              actionId={part.actionId}
                              // The server only emits a marker when the actions
                              // capability is on, and that same key gates approval
                              // — so a visible card is an approvable one. The
                              // route re-checks regardless; this only decides
                              // whether to render buttons or the pending state.
                              canApprove
                            />
                          ) : (
                            <AgentMarkdown
                              key={`${m.id}-text-${i}`}
                              text={part.text}
                            />
                          ),
                        )
                      : null}

                    {/* HOW THE TURN ENDED — as chrome under the answer, not as
                        text inside it. A budget stop, a cap, a timed-out lookup
                        or a refused repeat each get a bar here so a short answer
                        is not mistaken for a complete one. Absent on an ordinary
                        turn, so a normal answer renders exactly as before. The
                        `error` kind is hidden while the hook's own error bar
                        (below the thread) is showing the same event. */}
                    {m.outcome?.notices.length ? (
                      <TurnNotices
                        notices={m.outcome.notices}
                        hideErrorKind={Boolean(error) && m.id === lastAssistantId}
                      />
                    ) : null}

                    {/* Answer actions. Withheld only from the answer still being
                        WRITTEN — offering "copy" mid-stream copies half a sentence,
                        which reads as a bug rather than as eagerness.
                        `!isStreaming` alone was too broad: it stripped copy,
                        download and the elapsed time from every EARLIER answer in
                        the thread for as long as a new turn ran, so the controls
                        blinked out of finished answers nobody was touching. Only
                        the last assistant message can be the one streaming. */}
                    {/* WHICH PLAYBOOK SHAPED THIS ANSWER — as interface chrome,
                        never as text inside the answer.

                        A counselor needs to be able to see that team guidance was
                        applied: silent behaviour change is worse, and a playbook
                        producing bad answers has to be findable. But as a closing
                        LINE it ended up inside saved drafts, one copy-paste away
                        from being sent to a family:

                            Best regards,
                            [Your Name]
                            Following the team playbook: Deposit status first.

                        Rendering it here keeps the transparency and makes it
                        structurally impossible to paste — the same argument that
                        made drafts safe by giving the table no "sent" column.
                        Stripping it only on save would have left the same text
                        one manual copy away. */}
                    {answerOf(m.content).playbooks.length > 0 &&
                    (!isStreaming || m.id !== streamingMsgId) ? (
                      <div
                        className="mt-1.5 flex flex-wrap items-center gap-1.5"
                        data-testid="playbook-citation"
                      >
                        <SkyBody1 className="text-muted-foreground">Answered using</SkyBody1>
                        {answerOf(m.content).playbooks.map((name) => (
                          <SkyBadge
                            key={`${m.id}-pb-${name}`}
                            appearance="tint"
                            color="informative"
                            size="small"
                          >
                            {name}
                          </SkyBadge>
                        ))}
                      </div>
                    ) : null}

                    {(!isStreaming || m.id !== streamingMsgId) &&
                    answerOf(m.content).body.trim() ? (
                      <div className="mt-1.5 flex items-center gap-1">
                        <SkyButton
                          appearance="transparent"
                          size="small"
                          onClick={() => copyAnswer(m.id, m.content)}
                          title="Copy answer"
                          aria-label="Copy answer"
                          className="px-1 text-muted-foreground"
                        >
                          {copiedId === m.id ? (
                            <Check
                              className="h-3.5 w-3.5"
                              aria-hidden="true"
                            />
                          ) : (
                            <Copy
                              className="h-3.5 w-3.5"
                              aria-hidden="true"
                            />
                          )}
                        </SkyButton>
                        {/* Status in TEXT as well as the icon swap — the tick alone
                            carries the whole message otherwise, and colour/shape
                            alone is not an accessible signal. */}
                        {copiedId === m.id ? (
                          <span
                            className="text-sm text-muted-foreground"
                            role="status"
                          >
                            Copied
                          </span>
                        ) : null}

                        <SkyButton
                          appearance="transparent"
                          size="small"
                          onClick={() => downloadAnswer(m.content)}
                          title="Download answer"
                          aria-label="Download answer"
                          className="px-1 text-muted-foreground"
                        >
                          <Download
                            className="h-3.5 w-3.5"
                            aria-hidden="true"
                          />
                        </SkyButton>

                        {/* Keep it. Beside copy and download because it is the
                            same kind of act — taking the answer somewhere — and
                            the one that survives the chat scrolling away.
                            Renders nothing when the capability is off. */}
                        {/* The CLEAN body. A saved draft is the thing most likely
                            to be pasted into an email to a family, so the
                            citation must not be in it — see `answerOf`. */}
                        <SaveDraftButton
                          body={answerOf(m.content).body}
                          sessionId={currentSessionId ?? null}
                        />

                        {/* How long it took. Sits with the answer it describes
                            rather than in a status bar, so it is still there when
                            you scroll back through a thread. */}
                        {elapsedById[m.id] ? (
                          <span className="ml-auto text-sm tabular-nums text-muted-foreground">
                            {elapsedById[m.id]}s
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ),
              )}

              {/*
                Live activity strip. `label` is the server humanLabel; the raw
                tool name on the wire is never rendered.
              */}
              {/* Live progress only. The hook clears this the moment the turn
                  ends, so a finished answer is never trailed by a log of how it
                  was produced. */}
              {isStreaming && activity.length > 0 && (
                <ul
                  className="mb-4 space-y-1"
                  aria-live="polite"
                >
                  {activity.map((s) => (
                    <li
                      key={s.id}
                      className="flex items-center gap-2 text-sm text-muted-foreground"
                    >
                      {s.status === "running" ? (
                        <SkyLoader size="tiny" />
                      ) : (
                        <Check
                          className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400"
                          aria-hidden="true"
                        />
                      )}
                      <span>{s.label}</span>
                    </li>
                  ))}
                </ul>
              )}

              {(error || loadError) && (
                <div className="mb-4">
                  <SkyMessageBar
                    intent="error"
                    message={error ?? loadError ?? ""}
                  />
                </div>
              )}

              <div ref={endRef} />
            </div>
          )}
        </div>

        {/* ── Composer ───────────────────────────────────────────────── */}
        <div className={isEmpty ? "px-4 py-2" : "border-t border-border px-4 py-2"}>
          {/* ONE centring container for the pill AND its caption. They used to be
              two sibling `mx-auto max-w-3xl` boxes, which share a centre only for
              as long as nothing changes the width of either — so any padding or
              flex change on the pill silently pulled the two apart. Nested inside
              one wrapper they cannot disagree. */}
          <div className="mx-auto w-full max-w-3xl">
            {/* items-CENTER, not items-end.
              `items-end` pinned both children to the bottom of the pill: on one
              line the placeholder sat high with dead space beneath it, and once
              the textarea grew to four lines the send button stayed stuck at the
              bottom corner instead of tracking the middle. Centring makes the
              single-line case look deliberate and keeps the button beside the
              box at any height. */}
            {/* WHITE, not grey. `bg-muted/40` read as a disabled or read-only
                field — a muted ground is what this app uses for surfaces you
                cannot type into, and the one box on the page that most wants to
                look ready was wearing it. The border and the rounding carry the
                pill shape; the fill does not need to. */}
            <div className="rounded-2xl border border-border bg-background px-3 py-2">
              {/*
              A bare <textarea>, matching skySchedule's composer exactly.

              This is a deliberate, narrow exception to the Sky*-components rule.
              SkyTextarea carries its own internal padding, which makes
              scrollHeight exceed clientHeight by ~2px at rest — enough for the
              browser to paint a scrollbar track in an empty box. Three attempts
              to suppress it from the outside failed (manual height math,
              `autoResize`, then hiding the scrollbar chrome). skySchedule avoids
              the problem structurally: the PADDING LIVES ON THE WRAPPER and the
              textarea itself is bare and transparent, so its content height and
              its client height are identical and there is nothing to scroll.
            */}
              <textarea
                ref={taRef}
                value={input}
                rows={1}
                placeholder="How can I help you today?"
                aria-label="Message skyAgent"
                className="max-h-40 w-full resize-none bg-transparent py-1 text-sm leading-5 outline-none placeholder:text-muted-foreground"
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  // isComposing guards IME input. Japanese, Chinese and Korean
                  // users press Enter to COMMIT a candidate conversion, so without
                  // this the half-typed romaji is sent as the message and the
                  // conversion never completes.
                  if (e.nativeEvent.isComposing) return;
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submit(input);
                  }
                }}
                aria-describedby="skyagent-composer-hint"
              />
              {/* Control row beneath the field, matching skyCommand. The mode
                  switch has to sit WITH the composer, not off in a header: it
                  changes what the very next message is allowed to do, so it
                  belongs where that message is written. */}
              <div className="mt-1 flex items-center gap-2">
                {/* The "+" sits FIRST, left of the mode switch. Both are about
                    what the next message can do — the mode decides whether
                    skyAgent may propose a change, the "+" decides what it can
                    reach — so they belong together and before the message, not
                    beside the send button. It renders nothing at all when the
                    connectors capability is off. */}
                <ConnectorsMenu />
                {/* Attach this conversation to a prospect's record. Beside the
                    "+" because both are about what this thread reaches — the "+"
                    what it can read, the pin where it belongs. Renders nothing
                    until the thread exists, and nothing at all when the
                    capability is off. */}
                <ThreadPinControl sessionId={currentSessionId ?? null} />
                {/* A RECESSED track with an ELEVATED selected chip, which is the
                    segmented-control idiom everywhere it appears. It used to be the
                    other way round — a `bg-muted` chip on a `bg-background` track,
                    nested inside the composer's own `bg-muted/40` pill — so the
                    selected segment was ~6% darker than its track and, being the
                    DARKER of the two, read as "pressed" rather than "current". You
                    could not tell Chat from Cowork at a glance, which is a real
                    problem for a control that decides whether the next message is
                    allowed to propose writes.

                    The fill is `brand-80`, which the theme block names as the slot
                    for "active states, toggle fills, badges" — and amber is already
                    skyAgent's own colour on this screen (the Sparkles mark, the
                    prompt chips), so the lit segment reads as "the agent is in this
                    mode" rather than as a stray accent.

                    Its text is `brand-10`, not white. White on #f78a05 is ~2.6:1 and
                    fails AA for text this size; the near-black warm brown is ~9:1 on
                    the same fill and stays on-palette. Selection is also carried by
                    elevation and weight, so it never rests on hue alone — the same
                    rule as the dictation button's "Listening" text. */}
                <div
                  role="radiogroup"
                  aria-label="Response mode"
                  className="inline-flex rounded-lg bg-surface-strong p-0.5"
                >
                  {(
                    [
                      { id: "chat", label: "Chat", Icon: MessageSquare },
                      // A CIRCLED tick, not a bare one. A bare ✓ is the shape
                      // this interface uses for "done" and for "selected" — it
                      // sits beside Copy as the copied-confirmation two hundred
                      // pixels away — so on an UNSELECTED option it read as
                      // already-chosen, and the toggle looked like it was in a
                      // state it was not. A circled tick reads as an option.
                      { id: "cowork", label: "Cowork", Icon: CircleCheck },
                    ] as const
                  ).map(({ id, label, Icon }) => (
                    <button
                      key={id}
                      type="button"
                      role="radio"
                      aria-checked={mode === id}
                      onClick={() => chooseMode(id)}
                      className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-sm transition-colors duration-150 motion-reduce:transition-none ${
                        mode === id
                          ? "bg-brand-80 font-semibold text-brand-10 shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <Icon
                        className="h-3.5 w-3.5"
                        aria-hidden="true"
                      />
                      {label}
                    </button>
                  ))}
                </div>

                <div className="flex-1" />

                {/* Rendered only where the browser actually has the API — Safari
                    and Firefox get no button rather than one that fails on click. */}
                {dictation.supported ? (
                  <SkyButton
                    appearance={dictation.listening ? "secondary" : "transparent"}
                    size="small"
                    onClick={() => (dictation.listening ? dictation.stop() : dictation.start())}
                    aria-pressed={dictation.listening}
                    aria-label={dictation.listening ? "Stop dictating" : "Dictate a message"}
                    title={
                      dictation.listening
                        ? "Stop dictating"
                        : "Dictate a message. Your browser sends the audio to its speech service for transcription."
                    }
                    className={`px-1.5 ${dictation.listening ? "text-foreground" : "text-muted-foreground"}`}
                  >
                    <Mic
                      className="h-4 w-4"
                      aria-hidden="true"
                    />
                    {/* The listening state is carried by TEXT as well as the
                        button's fill, per the header's rule that status is never
                        colour alone. `bg-red-100 text-red-600` said "recording" to
                        a sighted user and nothing at all to anyone else. */}
                    {dictation.listening ? (
                      <span
                        className="text-sm"
                        role="status"
                      >
                        Listening
                      </span>
                    ) : null}
                  </SkyButton>
                ) : null}

                {isStreaming ? (
                  <SkyButton
                    appearance="secondary"
                    size="small"
                    title="Stop generating"
                    aria-label="Stop generating"
                    onClick={stop}
                  >
                    <Square
                      className="h-3.5 w-3.5"
                      aria-hidden="true"
                    />
                  </SkyButton>
                ) : (
                  <SkyButton
                    appearance="primary"
                    size="small"
                    disabled={!input.trim()}
                    title="Send"
                    aria-label="Send"
                    onClick={() => submit(input)}
                  >
                    <ArrowUp
                      className="h-3.5 w-3.5"
                      aria-hidden="true"
                    />
                  </SkyButton>
                )}
              </div>
            </div>
            {/* Keyboard hint, matching skyCommand. Worth the line: the composer
                HAS supported Shift+Enter for multi-line since it shipped and
                nothing said so, so people sent half-written questions and then
                sent the rest as a second message. `aria-describedby` on the
                textarea points here, so it is announced once on focus rather than
                read as loose text after the field. */}
            {/* Starter chips, BELOW the composer and empty-state only — the
                reference's placement. A previous edit to the footer copy removed
                this block wholesale; the panel's own test caught it, which is why
                that test asserts on a chip label rather than on a count. */}
            {isEmpty ? (
              // The bottom gap is an INLINE STYLE on purpose, and it is the third
              // attempt at it. `mt-3` on the caption below did nothing, `mt-6` did
              // nothing, `mb-5` here did nothing — only `mt-5` works, because that
              // class is already used elsewhere in the app. Utilities are not being
              // regenerated for this file, so a class that is not already compiled
              // silently resolves to no rule at all. An inline style cannot miss.
              <div
                style={{ marginBottom: "1.5rem" }}
                className="mt-5 flex flex-wrap items-center justify-center gap-2"
              >
                {STARTER_PROMPTS.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => submit(p.prompt)}
                    title={p.prompt}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border px-3.5 py-1.5 text-sm transition-colors duration-150 motion-reduce:transition-none hover:border-amber-500/50 hover:bg-muted"
                  >
                    <p.Icon
                      className="h-3.5 w-3.5 text-muted-foreground"
                      aria-hidden="true"
                    />
                    {p.label}
                  </button>
                ))}
              </div>
            ) : null}

            {/* Two lines, as the reference has them: what the current mode does,
                then the keyboard hint. They were briefly collapsed into one to cut
                clutter, but the mode caption is the only thing explaining a control
                sitting directly above it, and burying it mid-sentence made it
                read as boilerplate.

                The mode's NAME is now emphasised out of the sentence. The caption
                already led with it, but set in the same muted grey as the rest of
                the line, the one piece of prose on screen stating which mode is
                live did not look like a statement of state. */}
            <p className="mt-3 text-center text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">
                {mode === "chat" ? "Chat" : "Cowork"}
              </span>{" "}
              {mode === "chat"
                ? "answers and advises. Switch to Cowork to log activities, create tasks, or draft messages."
                : "proposes changes for you to approve. Nothing is written until you do."}
            </p>
            <p
              id="skyagent-composer-hint"
              className="mt-2 text-center text-sm text-muted-foreground"
            >
              Enter to send, Shift+Enter for new line
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChatPanel;
